// Standalone Momentum Chart page: a video player + selected-rally detail
// panel on top (split left/right), the full-width scrollable MomentumChart
// across the bottom half. Clicking a rally in the chart seeks the video to
// the start of that rally (the serve event's own recorded VideoPosition —
// same convention as VideoAnalysis.jsx's doSelectEvent) and highlights the
// rally's score cell (handled inside MomentumChart.jsx via the
// selectedRally/onRallyClick props).
import Hls from "hls.js";
import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useLocation, useNavigate } from "react-router-dom";
import MomentumChart from "../components/matches/MomentumChart";
import MomentumStatsPanel from "../components/matches/MomentumStatsPanel";
import { skillLabel } from "../components/utils/PointByPoint";
import { getTimingForEvent, toHlsStreamUrl } from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";

// Expands a youtu.be short link to the full watch URL (same regex as
// VideoAnalysis.jsx's local convertYouTubeUrl, adapted to local state here).
function expandYouTubeUrl(str) {
  if (!str || !str.includes("youtu.be")) return { url: str, isYoutube: false };
  const m = /.*\/([0-9a-zA-Z-_]*)/.exec(str);
  if (m?.[1]) return { url: "https://www.youtube.com/watch?v=" + m[1], isYoutube: true };
  return { url: str, isYoutube: false };
}

function MomentumChartPage() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { matches } = location.state ?? {};
  const match = matches?.[0];
  const playerRef = useRef();
  const hlsVideoRef = useRef(null);
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [isYoutube, setIsYoutube] = useState(false);
  const [selectedRally, setSelectedRally] = useState(null);
  const [isReady, setIsReady] = useState(false);
  // A seek requested before the player has signalled "ready" (e.g. the very
  // first rally click right after the page loads) is queued here and applied
  // once onReady/onCanPlay fires — without this, react-player's internal
  // <video> element isn't attached yet, getInternalPlayer() returns null, and
  // the seek silently does nothing, leaving the player stuck on a black frame.
  const pendingSeekRef = useRef(null);
  const hlsInstanceRef = useRef(null); // direct hls.js instance, mirrors VideoAnalysis.jsx/Playlist.jsx
  const sets = match?.sets ?? match?.drills ?? [];
  // Which stats tab is active: 0 = whole match (DVMate's BeachStatsView
  // currentSet===0 convention), N = that set number. Independent of
  // selectedRally — only the tab control drives which stats are shown.
  const [activeSetTab, setActiveSetTab] = useState(0);
  // computeMatchStats needs each set's true (positional) set number so its
  // returned rally identifiers line up with MomentumChart's own p.setNumber
  // tagging, even when only one set is passed in (a single stats tab).
  const setEntries = sets.map((set, idx) => ({ setNumber: idx + 1, set }));
  const statsSets = activeSetTab === 0 ? setEntries : setEntries.filter((s) => s.setNumber === activeSetTab);
  // Which stat (per team) is currently clicked, driving the rotation-cell
  // highlight on the chart below — clicking the same stat again clears it,
  // clicking a different one for that team replaces it (the other team's
  // highlight is untouched).
  const [highlightA, setHighlightA] = useState(null);
  const [highlightB, setHighlightB] = useState(null);
  const handleStatClick = (team, metricKey, rallies) => {
    const setHighlight = team === "A" ? setHighlightA : setHighlightB;
    setHighlight((prev) => (prev?.metric === metricKey ? null : { metric: metricKey, rallies }));
  };

  // Attaches hls.js to the raw <video> for .m3u8 sources — without this the
  // element has no src/MediaSource at all, so onCanPlay never fires and the
  // player is stuck black with isReady permanently false (Safari has native
  // HLS support and just needs a plain src).
  useEffect(() => {
    if (!videoFilePath?.endsWith(".m3u8")) return;
    const vid = hlsVideoRef.current;
    if (!vid) return;

    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    if (!Hls.isSupported()) {
      vid.src = videoFilePath;
    } else {
      const hls = new Hls({ xhrSetup: (xhr) => { xhr.withCredentials = false; } });
      hls.loadSource(videoFilePath);
      hls.attachMedia(vid);
      hlsInstanceRef.current = hls;
    }
    return () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  }, [videoFilePath]);

  useEffect(() => {
    if (!match) return;
    let onlineUrl = match.videoOnlineUrl;
    if (!onlineUrl) {
      const prefix = match.app === "VBStats" ? match.Guid : match.dvstring;
      try {
        const vobj = JSON.parse(localStorage.getItem(prefix + "_videoInfo"));
        if (vobj?.matchDVString === match.dvstring) onlineUrl = vobj.videoOnlineUrl;
      } catch {
        /* no cached video info for this match */
      }
    }
    if (!onlineUrl) return;
    const { url, isYoutube: yt } = expandYouTubeUrl(onlineUrl);
    setIsYoutube(yt);
    setIsReady(false);
    setVideoFilePath(toHlsStreamUrl(url));
  }, [match]);



  // Actual seek mechanics — only safe to call once the player is ready
  // (getInternalPlayer() returns null/undefined before then).
  const performSeek = (pos) => {
    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        vid.play().catch(() => {});
      }
      return;
    }
    if (isYoutube) {
      const yt = playerRef.current?.getInternalPlayer();
      yt?.seekTo?.(pos, true);
      yt?.playVideo?.();
      return;
    }
    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
      vid.play().catch(() => {});
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  // Seeks to the start of the selected rally (the serve event's own recorded
  // video position, minus the usual pre-roll lead time for a serve). Queues
  // the seek if the player isn't ready yet (see pendingSeekRef above).
  const seekToAndPlay = (seconds) => {
    if (seconds == null || !isFinite(seconds)) return;
    const pos = Math.max(0, seconds);
    if (!isReady) {
      pendingSeekRef.current = pos;
      return;
    }
    performSeek(pos);
  };

  // Fires once react-player (or the raw HLS <video>) has an underlying
  // element attached and ready to seek/play — applies any seek that was
  // requested too early (before this point getInternalPlayer() is null).
  const handlePlayerReady = () => {
    setIsReady(true);
    if (pendingSeekRef.current != null) {
      const pos = pendingSeekRef.current;
      pendingSeekRef.current = null;
      performSeek(pos);
    }
  };

  const handleRallyClick = (p) => {
    setSelectedRally(p);
    if (p.serveEvent?.VideoPosition) {
      const tm = getTimingForEvent(p.serveEvent);
      seekToAndPlay(p.serveEvent.VideoPosition - tm.leadTime);
    }
  };

  if (!match) {
    return (
      <div className="alert alert-error m-4">
        <span>{t("This report isn't supported on this browser yet — please use Safari.")}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-1rem)]">
      <div className="flex items-center justify-between px-2 py-1">
        <h2 className="font-bold text-lg">{t("Momentum Chart")}</h2>
        <button className="btn btn-sm" onClick={() => navigate(-1)}>
          {t("Close")}
        </button>
      </div>

      <div className="flex h-1/2 min-h-0 gap-2 px-2">
        {/* Left panel: video player, with the selected rally's summary
            overlaid as a caption bar along the bottom */}
        <div className="flex-1 relative bg-black rounded-md overflow-hidden">
          {videoFilePath ? (
            videoFilePath.endsWith(".m3u8") ? (
              <video
                ref={hlsVideoRef}
                controls
                onCanPlay={handlePlayerReady}
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            ) : (
              <ReactPlayer
                ref={playerRef}
                url={videoFilePath}
                playing
                width="100%"
                height="100%"
                controls
                onReady={handlePlayerReady}
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full text-base-content/50 text-sm">
              {t("No video available for this match.")}
            </div>
          )}
          {selectedRally && (
            <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-xs px-2 py-1 pointer-events-none">
              {t("Set")} {selectedRally.setNumber} — {selectedRally.preA}-{selectedRally.preB} —{" "}
              {t("Key event")}: {selectedRally.keyEvent?.Player?.NickName} {skillLabel(selectedRally.keyEvent)} —{" "}
              {selectedRally.sideout ? t("Sideout") : t("Break point")}
            </div>
          )}
        </div>

        {/* Right panel: a tab per set (number + score) plus a MATCH tab,
            selecting which set's (or the whole match's) DVMate-style team
            stats comparison is shown below */}
        <div className="flex-1 bg-base-200 rounded-md p-3 overflow-auto text-sm">
          <div role="tablist" className="tabs tabs-boxed tabs-xs flex-wrap mb-2">
            <button
              type="button"
              role="tab"
              className={"tab" + (activeSetTab === 0 ? " tab-active" : "")}
              onClick={() => setActiveSetTab(0)}
            >
              {t("Match")}
            </button>
            {sets.map((set, idx) => (
              <button
                key={idx}
                type="button"
                role="tab"
                className={"tab" + (activeSetTab === idx + 1 ? " tab-active" : "")}
                onClick={() => setActiveSetTab(idx + 1)}
              >
                {t("Set")} {idx + 1} ({set.HomeScore ?? 0}-{set.AwayScore ?? 0})
              </button>
            ))}
          </div>
          <MomentumStatsPanel
            sets={statsSets}
            teamAName={match.teamA?.Name}
            teamBName={match.teamB?.Name}
            t={t}
            activeStat={{ A: highlightA?.metric, B: highlightB?.metric }}
            onStatClick={handleStatClick}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto border-t border-base-300 mt-2">
        <MomentumChart
          matches={matches}
          selectedRally={selectedRally}
          onRallyClick={handleRallyClick}
          highlightA={highlightA?.rallies}
          highlightB={highlightB?.rallies}
        />
      </div>
    </div>
  );
}

export default MomentumChartPage;
